// Design-system primitives — single source for every rebuilt page.
// Markup/classes map 1:1 onto mockups/shared.css (the signed-off spec).
// Interaction-heavy components (Dropdown, inline editors, QuickAddRow,
// Stepper, cards/shelves) join this file with their first consuming page.

import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { callsheetHref } from '../lib/toolEmbed';
import { melbourneToday } from '../../shared/dates';
import { useIsMobile } from '../lib/useIsMobile';
import { DP_DOW, MONTHS, formatDate, formatDayMonth, monthGrid, shiftDays, shiftMonth, todayLabel } from '../lib/calendar';
import { formatMoney, formatMoneyExact, parseMoney } from '../lib/moneyFormat';

// Pure helpers live in lib/ (no component imports → no ESM cycle with the
// sheets); re-exported here so every existing call site keeps working.
export {
  DAYS,
  DP_DOW,
  MONTHS,
  addDays,
  formatDate,
  formatDayMonth,
  formatProjectNumber,
  formatWeekdayDate,
  formatWeekdayDay,
  isoOf,
  monthGrid,
  shiftDays,
  shiftMonth,
  todayLabel,
} from '../lib/calendar';
export { formatMoney, formatMoneyExact, parseMoney } from '../lib/moneyFormat';
import { DateSheet, type DateChip } from './DateSheet';
import { FieldLabelContext, FieldSheet, guessFieldKind, type FieldKind } from './FieldSheet';
import { PickerSheet } from './PickerSheet';
import { Sheet, SheetButton } from './Sheet';
import {
  PHASE_PILL_LABELS,
  PHASE_PROGRESS,
  type Brand,
  type ProjectPhase,
} from '../../shared/types';

// ── Page chrome ──────────────────────────────────────────────────────────

/** One of the (max three) 40px icon buttons on the mobile header, in the
    order the page gives them — conventionally search, filter, "···". */
export interface PageHeaderAction {
  key: 'search' | 'filter' | 'menu' | (string & {});
  icon: ReactNode;
  label: string;
  onPress: () => void;
  active?: boolean;
}

/** Select mode (mockup §3.1 D): the mobile header becomes the count plus
    "Select all" and "Done"; the bulk actions dock where the tab bar was. */
export interface PageHeaderSelection {
  count: number;
  onSelectAll?: () => void;
  onDone: () => void;
}

export function PageHeader({
  title,
  count,
  liveLabel = 'Synced',
  liveError = false,
  liveWarn = false,
  mobileActions,
  selection,
  children,
}: {
  title: string;
  count?: number | string;
  /** Header-right status text ("Integration OK" on Dashboards). */
  liveLabel?: string;
  liveError?: boolean;
  /** Amber dot for a degraded-but-readable state (Meetings: not connected). */
  liveWarn?: boolean;
  /** Rendered only at ≤880px, max three, in the order given (§0.5.1). */
  mobileActions?: PageHeaderAction[];
  /** When `count > 0` the mobile header switches to select mode. */
  selection?: PageHeaderSelection;
  children?: ReactNode;
}) {
  const isMobile = useIsMobile();

  // Mobile (§0.5.1, mockup §3.1 A/D): the sync eyebrow moves out of
  // .header-right into its own strip above a display-type title row.
  if (isMobile) {
    const selecting = selection !== undefined && selection.count > 0 ? selection : null;
    return (
      <header className="page-header-m">
        <div className={`synced${liveError ? ' err' : liveWarn ? ' warn' : ''}`}>
          <i aria-hidden="true" />
          {liveLabel} · {todayLabel()}
        </div>
        <div className={`ph${selecting ? ' ph-select' : ''}`}>
          <h1>
            {selecting ? (
              `${selecting.count} selected`
            ) : (
              <>
                {title}
                {count !== undefined && <small>{count}</small>}
              </>
            )}
          </h1>
          {children}
          <div className="acts">
            {selecting ? (
              <>
                {selecting.onSelectAll && (
                  <button type="button" className="cbtn acc" onClick={selecting.onSelectAll}>
                    Select all
                  </button>
                )}
                <button type="button" className="cbtn" onClick={selecting.onDone}>
                  Done
                </button>
              </>
            ) : (
              mobileActions?.slice(0, 3).map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`ib${a.active ? ' on' : ''}`}
                  aria-label={a.label}
                  aria-pressed={a.active}
                  onClick={a.onPress}
                >
                  {a.icon}
                </button>
              ))
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="page-header">
      <span className="page-title">
        {title}
        {count !== undefined && <span className="page-title-count"> {count}</span>}
      </span>
      {children}
      <div className="header-right">
        <span className="live">
          <span className={`live-dot${liveError ? ' err' : liveWarn ? ' warn' : ''}`} />
          {liveLabel}
        </span>
        <span>{todayLabel()}</span>
      </div>
    </header>
  );
}

/** Quiet loading placeholder (Phase 1.3) — a few pulsing rows shown while a
    list page's first fetch is in flight, replacing the old "0 / nothing here"
    flash before data arrives. Reused across every list page. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div className="skeleton-row" key={i} />
      ))}
    </div>
  );
}

/** Collapsible rail/detail group with the RailGroup persistence contract
    (§0.5.5) — lives in its own file, exposed here with the rest of the
    page chrome so Phase B imports it from one place. */
export { AccordionGroup } from './AccordionGroup';

// ── Group bands ──────────────────────────────────────────────────────────

export function Group({
  title,
  count,
  link,
  className,
  style,
  children,
}: {
  title: string;
  count?: number | string;
  link?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div className={`group${className ? ` ${className}` : ''}`} style={style}>
      <div className="group-head">
        <span className="group-title">{title}</span>
        {count !== undefined && <span className="group-count">{count}</span>}
        {link}
      </div>
      {children}
    </div>
  );
}

export function GroupLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="group-link" to={to}>
      {children}
    </Link>
  );
}

// ── Pills & dots ─────────────────────────────────────────────────────────

/** `cleanup` is the Clean up phase's dashed green pill. */
export type PillTone = 'accent' | 'gold' | 'danger' | 'ok' | 'cleanup' | undefined;

export function Pill({ tone, children }: { tone?: PillTone; children: ReactNode }) {
  return <span className={`pill${tone ? ` ${tone}` : ''}`}>{children}</span>;
}

/** hsp / ss brand chip — teal for HSP, quiet for SS. Shared by Projects and
    the Social library, which mix both brands in one list. */
export function BrandBadge({ brand }: { brand: Brand }) {
  return <Pill tone={brand === 'hsp' ? 'accent' : undefined}>{brand}</Pill>;
}

// ── Vitals strip ─────────────────────────────────────────────────────────

export function Vitals({ children }: { children: ReactNode }) {
  return <div className="vitals">{children}</div>;
}

export function Vital({
  label,
  value,
  tone,
  sub,
  to,
}: {
  label: string;
  value: ReactNode;
  tone?: 'accent' | 'gold' | 'danger' | 'ok';
  sub?: ReactNode;
  to?: string;
}) {
  const body = (
    <>
      <span className="vital-label">{label}</span>
      <span className={`vital-value${tone ? ` ${tone}` : ''}`}>{value}</span>
      {sub !== undefined && <span className="vital-sub">{sub}</span>}
    </>
  );
  return to ? (
    <Link className="vital" to={to}>
      {body}
    </Link>
  ) : (
    <a className="vital">{body}</a>
  );
}

// ── Activity ─────────────────────────────────────────────────────────────

export function ActivityRow({ time, children }: { time: string; children: ReactNode }) {
  return (
    <div className="activity-row">
      <span className="activity-time">{time}</span>
      <span className="activity-text">{children}</span>
    </div>
  );
}

// ── Timeline ─────────────────────────────────────────────────────────────

export function Timeline({ children }: { children: ReactNode }) {
  return <div className="timeline">{children}</div>;
}

/** `callsheetUrl` (2F, R6.1) marks a call sheet milestone: its summary
    ("Shoot wrapped · Clinic stories") splits into a bright head and a faint
    tail, and a "Call sheet ↗" link opens the sheet in the embedded tool. */
export function TimelineRow({ date, callsheetUrl, children }: { date: string; callsheetUrl?: string | null; children: ReactNode }) {
  let text = children;
  if (callsheetUrl && typeof children === 'string') {
    const cut = children.indexOf(' · ');
    text = cut < 0 ? <span className="hl">{children}</span>
      : <><span className="hl">{children.slice(0, cut)}</span> <span className="src">{children.slice(cut + 1)}</span></>;
  }
  return (
    <div className="tl-row">
      <span className="tl-date">{date}</span>
      <span className="tl-text">
        {text}
        {callsheetUrl && <Link className="tl-link" to={callsheetHref(callsheetUrl)}>Call sheet ↗</Link>}
      </span>
    </div>
  );
}

// ── Detail-rail fields ───────────────────────────────────────────────────

export function RailField({
  label,
  children,
  valueClass,
}: {
  label: string;
  children: ReactNode;
  valueClass?: string;
}) {
  const isMobile = useIsMobile();

  // Mobile (§0.3.3, mockup §4.2 E): the rail becomes a list of
  // `LABEL · value · ›` rows. The label goes into context so the Inline*
  // editor inside can title its sheet without the call site repeating it.
  if (isMobile) {
    return (
      <FieldLabelContext.Provider value={{ label }}>
        <div className="frow-host">
          <div className="frow-label">{label}</div>
          <div className={`frow-value${valueClass ? ` ${valueClass}` : ''}`}>{children}</div>
        </div>
      </FieldLabelContext.Provider>
    );
  }

  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div className={`field-value${valueClass ? ` ${valueClass}` : ''}`}>{children}</div>
    </div>
  );
}

export function MetaStamp({ children }: { children: ReactNode }) {
  return <div className="meta-stamp">{children}</div>;
}

/** Quiet rail-bottom trash action with a two-step confirm (Henry's ask,
    2026-06-12 — projects/leads/invoices that aren't going ahead). Items land
    in Settings → Trash, same soft-delete the API already does. */
export function TrashAction({
  label,
  onConfirm,
  disabled = false,
  title,
}: {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  title?: string;
}) {
  const [arming, setArming] = useState(false);
  useEffect(() => {
    if (!arming) return;
    const t = setTimeout(() => setArming(false), 4000);
    return () => clearTimeout(t);
  }, [arming]);
  return (
    <button
      type="button"
      className="trash-action"
      disabled={disabled}
      title={title}
      onClick={() => (arming ? onConfirm() : setArming(true))}
    >
      {arming ? 'Click again to confirm' : label}
    </button>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────

type ButtonEl = ReactElement<{ className?: string; children?: ReactNode }>;

/** The Modal footer convention is `<>Cancel .btn · primary .btn</>`. On
    mobile the mockups (§3.3 H) carry that pair in the sheet header and
    repeat the primary above the keyboard — so when the footer is exactly
    that pair, split it; anything else (three buttons, a delete, a bare
    node) lands in the sheet footer as-is. */
function splitModalFoot(foot: ReactNode): { left?: ReactNode; right?: ReactNode; footer?: ReactNode } {
  if (foot === undefined || foot === null || foot === false) return {};
  const inner =
    isValidElement(foot) && foot.type === Fragment
      ? (foot.props as { children?: ReactNode }).children
      : foot;
  const nodes = Children.toArray(inner).filter((n) => isValidElement(n)) as ButtonEl[];
  if (nodes.length === 2 && nodes.every((n) => n.type === 'button')) {
    const [cancel, primary] = nodes;
    const ca = cancel.props.className ?? '';
    const cb = primary.props.className ?? '';
    if (/\bbtn\b/.test(ca) && !/\bprimary\b/.test(ca) && /\bprimary\b/.test(cb)) {
      // The footer copy keeps the caller's classes (a "btn primary danger"
      // stays red); the header copy maps primary → accent, danger → danger.
      return {
        left: cloneElement(cancel, { className: 'sheet-act-btn' }),
        right: cloneElement(primary, { className: `sheet-act-btn ${/\bdanger\b/.test(cb) ? 'danger' : 'accent'}` }),
        footer: cloneElement(primary, { className: cb }),
      };
    }
  }
  return { footer: foot };
}

export function Modal({
  title,
  context,
  width,
  onClose,
  hint,
  foot,
  mobile = 'full',
  children,
}: {
  title: string;
  context?: string;
  width?: number;
  onClose: () => void;
  hint?: string;
  foot?: ReactNode;
  /** ≤880px render (Phase 0 §0.2.2): forms are full-height sheets; set
      'half' for short pickers/confirms. Desktop ignores it. */
  mobile?: 'full' | 'half';
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isMobile) return; // Sheet owns Escape on mobile (top sheet only).
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isMobile]);

  if (isMobile) {
    const split = splitModalFoot(foot);
    // The desktop footer hint ("At least 8 characters", "3 tasks selected")
    // is real guidance — it rides above the sheet's buttons.
    const footer =
      hint || split.footer ? (
        <>
          {hint && <span className="modal-hint sheet-hint">{hint}</span>}
          {split.footer}
        </>
      ) : undefined;
    return (
      <Sheet
        open
        onClose={onClose}
        variant={mobile}
        title={title}
        context={context}
        leftAction={split.left}
        rightAction={split.right ?? <SheetButton onClick={onClose}>Close</SheetButton>}
        footer={footer}
        className="sheet-modal"
      >
        <div className="modal-body">{children}</div>
      </Sheet>
    );
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={width ? { width } : undefined} role="dialog" aria-modal>
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          {context && <span className="modal-context">{context}</span>}
          <span className="modal-esc">ESC</span>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">
          {hint && <span className="modal-hint">{hint}</span>}
          {foot}
        </div>
      </div>
    </div>
  );
}

// ── Segmented control ────────────────────────────────────────────────────

export function Seg<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`seg-item${o.value === value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Project gallery card (01-overview; Projects index extends in phase 4) ─

export function ProjectCard({
  phase,
  dueLabel,
  name,
  clientName,
  openTasks,
  to,
}: {
  phase: ProjectPhase;
  dueLabel: string;
  name: string;
  clientName: string;
  openTasks: number;
  to: string;
}) {
  const tone = phase === 'post' ? 'accent' : phase === 'shoot' ? 'gold' : undefined;
  const progress = PHASE_PROGRESS[phase];
  const fill = phase === 'shoot' ? 'fill-gold' : phase === 'lead' ? 'fill-dim' : 'fill-accent';
  const tasksLabel =
    phase === 'on_hold'
      ? 'Paused'
      : openTasks === 0
        ? 'No open tasks'
        : `${openTasks} open task${openTasks === 1 ? '' : 's'}`;
  return (
    <Link className="project-card" to={to}>
      <div className="card-top">
        <Pill tone={tone}>{PHASE_PILL_LABELS[phase]}</Pill>
        <span className="card-due">{dueLabel}</span>
      </div>
      <div className="card-title">{name}</div>
      <div className="card-client">{clientName}</div>
      <div className="card-footer">
        <span className="card-tasks">{tasksLabel}</span>
        <span className="card-progress">
          {progress !== null && <span className={fill} style={{ width: `${progress * 100}%` }} />}
        </span>
      </div>
    </Link>
  );
}

// ── Toolbar pieces ───────────────────────────────────────────────────────

export function Tabs<T extends string>({
  options,
  value,
  onChange,
}: {
  /** `count` trails the label in mono (Projects → Clean up). */
  options: readonly { value: T; label: string; count?: number }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const isMobile = useIsMobile();

  // ≤880px (§0.5.1, mockup §3.1 A): the segmented control becomes a
  // horizontally scrolling chip row. Same buttons, same onChange.
  if (isMobile) {
    return (
      <div className="mtabs">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`t${o.value === value ? ' on' : ''}`}
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="tabs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`tab${o.value === value ? ' active' : ''}`}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className="tab-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="search">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" />
      </svg>
      <input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function VitalsSlim({ children }: { children: ReactNode }) {
  // Interleave dividers between the .vs spans, per the mockup.
  const items = Array.isArray(children) ? children.filter(Boolean) : [children];
  return (
    <div className="vitals-slim">
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="vs-divider" />}
          {item}
        </Fragment>
      ))}
    </div>
  );
}

export function VS({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: ReactNode;
  tone?: 'gold' | 'danger' | 'accent' | 'ok';
  title?: string;
}) {
  return (
    <span className="vs" title={title}>
      <span className="vs-label">{label}</span>
      <span className={`vs-value${tone ? ` ${tone}` : ''}`}>{value}</span>
    </span>
  );
}

// ── Dropdown (02-lead-detail spec: panel-3 menu, dot + tick items) ───────

export interface DropdownItem<T extends string> {
  value: T;
  label: string;
  dotColor?: string;
  /** Mobile PickerSheet only (Phase 0 §0.3.1): mono secondary line under the
      label, e.g. "Macedon Ranges Glass · #0232". Desktop ignores it. */
  sub?: string;
  /** Mobile PickerSheet only: items sharing a group render under one mono
      eyebrow header ("Active", "Recently delivered"). Desktop ignores it. */
  group?: string;
}

/** Anchor rect + menu box in viewport coordinates. */
export interface PlaceInput {
  anchor: { top: number; bottom: number; left: number; width: number };
  height: number;
  width: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Space between anchor and menu (default 4). */
  gap?: number;
  /** Minimum breathing room against the viewport edge (default 8). */
  gutter?: number;
  /** Direction the menu already opened in — kept while it still fits, so a
      searchable list that shrinks as you type doesn't jump across the field. */
  prefer?: 'down' | 'up' | null;
}

/** Flipped menus return `bottom`, never `top`, so a list that shrinks while
    open (search filtering) stays glued to the field instead of drifting. */
export interface Placement {
  dir: 'down' | 'up';
  top?: number;
  bottom?: number;
  left: number;
}

/** Pure viewport placement maths for the body-level menu portals — no DOM,
    so it is unit-testable and shared by every Dropdown call site. */
export function placeMenu({
  anchor,
  height,
  width,
  viewportWidth,
  viewportHeight,
  gap = 4,
  gutter = 8,
  prefer = null,
}: PlaceInput): Placement {
  const fitsBelow = anchor.bottom + gap + height <= viewportHeight - gutter;
  const fitsAbove = anchor.top - gap - height >= gutter;
  const left = Math.max(gutter, Math.min(anchor.left, viewportWidth - width - gutter));
  const down = (): Placement => ({ dir: 'down', top: anchor.bottom + gap, left });
  const up = (): Placement => ({ dir: 'up', bottom: viewportHeight - anchor.top + gap, left });
  // Stick with the open direction while it still fits (hysteresis).
  if (prefer === 'up' && fitsAbove) return up();
  if (prefer === 'down' && fitsBelow) return down();
  // Down wins ties: a menu that fits neither way still opens downward, where
  // the .dd-list scroller can take over.
  if (fitsBelow || !fitsAbove) return down();
  return up();
}

interface MenuPos extends Placement {
  minWidth: number;
}

export function Dropdown<T extends string>({
  items,
  value,
  onSelect,
  onClose,
  searchable,
  anchorRef,
  onPlacement,
  label,
  keyboardNavigation = false,
}: {
  /** Opt-in action menu keyboard controls; existing select behavior stays unchanged. */
  keyboardNavigation?: boolean;
  items: readonly DropdownItem<T>[];
  value?: T | null;
  onSelect: (v: T) => void;
  onClose: () => void;
  /** Type-to-filter + scrolling list — use for long lists (clients, projects). */
  searchable?: boolean;
  /** Element to position the body-level portal against. Defaults to the
      wrapper the menu is written inside (`.dropdown-anchor`). */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Fires when the menu settles up or down — SelectField spins its caret. */
  onPlacement?: (dir: 'down' | 'up') => void;
  /** Mobile PickerSheet only (§0.3.1): the sheet title — the field's own
      label. Defaults to "Select". Also names opt-in desktop action menus. */
  label?: string;
}) {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const holderRef = useRef<HTMLSpanElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<MenuPos | null>(null);
  const placed = pos !== null;
  const dirRef = useRef<'down' | 'up' | null>(null);
  // Held in a ref so an inline callback can't re-trigger the layout effect.
  const placementRef = useRef(onPlacement);
  placementRef.current = onPlacement;

  // The element the menu hangs from: an explicit ref, else the wrapper the
  // placeholder span was written inside (.dropdown-anchor, .project-select).
  const resolveAnchor = () => anchorRef?.current ?? holderRef.current?.parentElement ?? null;

  useEffect(() => {
    if (isMobile) return; // PickerSheet owns dismissal (backdrop, Escape) on mobile.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // A mousedown on the anchor is left to its own toggle — otherwise the
      // outside-close fires first and the click reopens a fresh menu.
      if (ref.current?.contains(target) || resolveAnchor()?.contains(target)) return;
      onClose();
    };
    // Capture phase so Modal's own Escape handler doesn't also fire.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (keyboardNavigation) resolveAnchor()?.querySelector<HTMLElement>('button')?.focus();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, isMobile, keyboardNavigation]);

  const visible = query.trim()
    ? items.filter((i) => i.label.toLowerCase().includes(query.trim().toLowerCase()))
    : items;

  // Fixed positioning measured against the viewport: escapes the modal's
  // overflow:hidden (which clipped absolutely positioned menus) and flips
  // upward when there's no room below.
  useLayoutEffect(() => {
    if (isMobile) return; // No portal to place — the PickerSheet fills the screen.
    const place = () => {
      const menu = ref.current;
      const anchor = resolveAnchor();
      if (!menu || !anchor) return;
      const a = anchor.getBoundingClientRect();
      const minWidth = Math.max(180, a.width);
      const next = placeMenu({
        anchor: { top: a.top, bottom: a.bottom, left: a.left, width: a.width },
        height: menu.offsetHeight,
        // The rendered menu is at least as wide as the field it drops from.
        width: Math.max(menu.offsetWidth, minWidth),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        prefer: dirRef.current,
      });
      setPos({ ...next, minWidth });
      if (dirRef.current !== next.dir) {
        dirRef.current = next.dir;
        placementRef.current?.(next.dir);
      }
    };
    place();
    window.addEventListener('resize', place);
    // Capture phase: page content scrolls inside .page-body, not window.
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
    // Filtering shrinks the list, so a flipped menu has to re-measure. Keyed
    // on the count, not `items` — call sites build that array inline every
    // render, and re-placing on each render would loop through setPos.
  }, [anchorRef, visible.length, isMobile]);

  // The menu mounts visibility:hidden until measured, and focus() is a no-op
  // inside a hidden subtree — so autoFocus never lands. Focus the search box
  // once the menu has been placed instead.
  useLayoutEffect(() => {
    if (!isMobile && placed) {
      if (searchable) searchRef.current?.focus();
      else if (keyboardNavigation) ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [placed, isMobile, searchable, keyboardNavigation]);

  const pick = (v: T) => {
    onSelect(v);
    onClose();
  };

  // Mobile (§0.3.1, mockup §3.2 G): every menu is a half sheet of 54px rows.
  if (isMobile) {
    return (
      <PickerSheet
        open
        title={label ?? 'Select'}
        items={items}
        value={value}
        onSelect={onSelect}
        onClose={onClose}
        searchable={searchable}
      />
    );
  }

  const Item = keyboardNavigation ? 'button' : 'div';
  return (
    <>
      {/* Marks the spot in the tree the menu was written at — its parent is
          the anchor when no anchorRef is given. */}
      <span ref={holderRef} style={{ display: 'none' }} />
      {createPortal(
        <div
          className={`dropdown${keyboardNavigation ? ' dropdown-keyboard' : ''}`}
          role={keyboardNavigation ? 'menu' : undefined}
          aria-label={keyboardNavigation ? label : undefined}
          ref={ref}
          onKeyDown={keyboardNavigation ? (e) => {
            const buttons = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
            const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
            let next: number | undefined;
            if (e.key === 'ArrowDown') next = (index + 1) % buttons.length;
            if (e.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
            if (e.key === 'Home') next = 0;
            if (e.key === 'End') next = buttons.length - 1;
            if (next !== undefined) { e.preventDefault(); buttons[next]?.focus(); }
            if (e.key === 'Tab') {
              // Resume the normal tab sequence beside the opener.
              resolveAnchor()?.querySelector<HTMLElement>('button')?.focus();
              onClose();
            }
          } : undefined}
          // Portals bubble React events to the anchor's toggle and the modal
          // overlay, which would reopen or close things behind the menu.
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={
            pos
              ? { position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, minWidth: pos.minWidth }
              : { position: 'fixed', visibility: 'hidden' }
          }
        >
          {searchable && (
            <input
              className="dd-search"
              ref={searchRef}
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter picks the single (or first) match — fastest path when typing.
                if (e.key === 'Enter' && visible.length > 0) pick(visible[0].value);
              }}
            />
          )}
          <div className="dd-list">
            {visible.map((item) => (
              <Item
                type={keyboardNavigation ? 'button' : undefined}
                role={keyboardNavigation ? 'menuitem' : undefined}
                tabIndex={keyboardNavigation ? -1 : undefined}
                key={item.value}
                className="dd-item"
                onClick={(e) => {
                  e.stopPropagation();
                  pick(item.value);
                }}
              >
                {item.dotColor && <span className="pdot" style={{ background: item.dotColor }} />}
                {item.label}
                {value === item.value && <span className="tick">✓</span>}
              </Item>
            ))}
            {visible.length === 0 && <div className="dd-item" style={{ color: 'var(--text-faint)', cursor: 'default' }}>No matches.</div>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ── Sort button (feedback round two §1/§3) ───────────────────────────────
// "Sort · Number ↓ ▾" — the label always shows the live key so the order
// can be read without opening it. Clicking the arrow flips direction;
// clicking anywhere else opens a menu of keys, each in its natural
// direction (newest / soonest / A–Z). Picking a key resets to that natural
// direction. Pages own the SortValue and default it per tab.

export interface SortOption {
  key: string;
  /** Short label for the button, e.g. "Number", "Closed". */
  label: string;
  /** Direction that reads best for this key — what picking it selects. */
  natural: 'asc' | 'desc';
  /** Menu suffix, e.g. "newest first", "soonest first", "A–Z". */
  naturalLabel: string;
  /** Optional longer menu label ("Closed date"); defaults to `label`. */
  menuLabel?: string;
}

export interface SortValue {
  key: string;
  dir: 'asc' | 'desc';
}

export function SortButton({
  options,
  value,
  onChange,
  disabled,
}: {
  options: readonly SortOption[];
  value: SortValue;
  onChange: (next: SortValue) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const active = options.find((o) => o.key === value.key) ?? options[0];
  const flipped = value.dir === 'asc' ? 'desc' : 'asc';
  const openMenu = () => {
    if (!disabled) setOpen((o) => !o);
  };
  // Two real buttons side by side (HTML forbids a button inside a button),
  // styled as one dashed pill so it reads like the mockup's single control.
  return (
    <span className="dropdown-anchor">
      <span className={`sort-ctl${disabled ? ' disabled' : ''}`}>
        <button
          type="button"
          className="sort-main"
          disabled={disabled}
          onClick={openMenu}
          aria-haspopup="menu"
          aria-expanded={open}
          title={`Sorted by ${active.label.toLowerCase()} · ${value.dir === 'asc' ? 'ascending' : 'descending'}`}
        >
          Sort · <span className="k">{active.label}</span>
        </button>
        <button
          type="button"
          className="sort-dir"
          disabled={disabled}
          aria-label="Reverse order"
          title="Reverse order"
          onClick={() => onChange({ key: value.key, dir: flipped })}
        >
          {value.dir === 'desc' ? '↓' : '↑'}
        </button>
        <span className="caret" onClick={openMenu} aria-hidden="true">▾</span>
      </span>
      {open && !disabled && (
        <Dropdown
          label="Sort by"
          items={options.map((o) => ({ value: o.key, label: `${o.menuLabel ?? o.label} · ${o.naturalLabel}` }))}
          value={value.key}
          onSelect={(key) => {
            const opt = options.find((o) => o.key === key);
            if (opt) onChange({ key: opt.key, dir: opt.natural });
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

// ── Filter button + chips (04-tasks toolbar pattern) ─────────────────────

export interface FilterField {
  key: string;
  label: string;
  items: DropdownItem<string>[];
  searchable?: boolean;
}

/** "+ Filter" → pick a field → pick a value. Active filters render as chips
    next to the button; × clears one. */
export function FilterBar({
  fields,
  active,
  onChange,
}: {
  fields: FilterField[];
  active: Record<string, string | null>;
  onChange: (key: string, value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<FilterField | null>(null);

  const chips = fields
    .map((f) => {
      const value = active[f.key];
      if (!value) return null;
      const item = f.items.find((i) => i.value === value);
      return { field: f, label: item?.label ?? value };
    })
    .filter(Boolean) as { field: FilterField; label: string }[];

  return (
    <>
      {chips.map(({ field: f, label }) => (
        <span className="filter-chip" key={f.key}>
          {f.label}: {label}
          <span className="x" onClick={() => onChange(f.key, null)}>
            ×
          </span>
        </span>
      ))}
      <span className="dropdown-anchor">
        <button
          className="btn filter"
          onClick={() => {
            setField(null);
            setOpen((o) => !o);
          }}
        >
          + Filter
        </button>
        {open && !field && (
          <Dropdown
            label="Filter by"
            items={fields.map((f) => ({ value: f.key, label: f.label }))}
            onSelect={(key) => {
              const next = fields.find((f) => f.key === key) ?? null;
              // Re-open at value stage; Dropdown closes itself on select.
              setTimeout(() => {
                setField(next);
                setOpen(true);
              }, 0);
            }}
            onClose={() => setOpen(false)}
          />
        )}
        {open && field && (
          <Dropdown
            label={field.label}
            items={field.items}
            value={active[field.key]}
            onSelect={(value) => onChange(field.key, value)}
            onClose={() => {
              setOpen(false);
              setField(null);
            }}
            searchable={field.searchable}
          />
        )}
      </span>
    </>
  );
}

// ── Inline editing (ledger: click field → input, Enter save / Esc cancel /
//    blur save, PATCH per field, optimistic with rollback) ─────────────────

function useInlineEdit(current: string, onSave: (raw: string) => void) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  const open = () => {
    setDraft(current);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    if (draft !== current) onSave(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };
  return { editing, draft, setDraft, open, commit, onKeyDown };
}

/** The tappable value an Inline* editor shows at ≤880px (§0.3.3, mockup
    §4.2 E): the right half of a rail row — value, or a faint "Add", and the
    chevron that says a sheet is behind it. Used with or without a RailField,
    so table cells and the Stats goal cards keep working unchanged. */
function FieldTap({
  onPress,
  valueClass,
  style,
  empty,
  children,
}: {
  onPress: () => void;
  valueClass?: string;
  style?: CSSProperties;
  empty?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`frow-tap${valueClass ? ` ${valueClass}` : ''}${empty ? ' empty' : ''}`}
      style={style}
      onClick={onPress}
    >
      <span className="frow-tap-v">{children}</span>
      <span className="chev" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

const ADD = <span className="frow-add">Add</span>;

export function InlineText({
  value,
  display,
  onSave,
  mono,
  placeholder,
  valueClass,
  kind,
  label,
}: {
  value: string | null;
  /** Optional custom renderer for display mode (links etc.). */
  display?: ReactNode;
  onSave: (v: string | null) => void;
  mono?: boolean;
  placeholder?: string;
  valueClass?: string;
  /** Mobile FieldSheet only (§0.3.3): which keyboard the sheet opens with.
      Defaults to a guess from the current value (email / tel / url). */
  kind?: FieldKind;
  /** Mobile FieldSheet only: sheet title. Defaults to the RailField label. */
  label?: string;
}) {
  const isMobile = useIsMobile();
  const railLabel = useContext(FieldLabelContext)?.label;
  const [sheet, setSheet] = useState(false);
  const edit = useInlineEdit(value ?? '', (raw) => onSave(raw.trim() === '' ? null : raw.trim()));

  if (isMobile) {
    return (
      <>
        <FieldTap valueClass={valueClass} empty={!value && display === undefined} onPress={() => setSheet(true)}>
          {display ?? (value || ADD)}
        </FieldTap>
        <FieldSheet
          open={sheet}
          title={label ?? railLabel ?? 'Edit'}
          kind={kind ?? guessFieldKind(value)}
          value={value ?? ''}
          placeholder={placeholder}
          onSave={(raw) => onSave(raw.trim() === '' ? null : raw.trim())}
          onClose={() => setSheet(false)}
        />
      </>
    );
  }

  if (edit.editing) {
    return (
      <>
        <input
          className={`inline-input${mono ? ' mono' : ''}`}
          value={edit.draft}
          placeholder={placeholder}
          onChange={(e) => edit.setDraft(e.target.value)}
          onKeyDown={edit.onKeyDown}
          onBlur={edit.commit}
          autoFocus
        />
        <div className="inline-hint">Enter to save · Esc to cancel</div>
      </>
    );
  }
  return (
    <div className={`field-value editable${valueClass ? ` ${valueClass}` : ''}`} onClick={edit.open}>
      {display ?? (value || <span className="dim" style={{ color: 'var(--text-faint)' }}>—</span>)}
    </div>
  );
}

/** One contact line: entity override, or the client's value shown faint
    (inherit-from-client pattern — projects since 0008, invoices since 0009). */
export function ContactLine({
  value,
  fallback,
  mono,
  accent,
  onSave,
  kind,
  label,
}: {
  value: string | null;
  fallback?: string | null;
  mono?: boolean;
  accent?: boolean;
  onSave: (v: string | null) => void;
  /** Mobile FieldSheet keyboard — contact rows are email / tel / url. */
  kind?: FieldKind;
  /** Mobile FieldSheet title (falls back to the RailField label). */
  label?: string;
}) {
  return (
    <div style={{ marginTop: 2 }}>
      <InlineText
        value={value}
        mono={mono}
        kind={kind}
        label={label}
        valueClass={mono ? 'mono' : undefined}
        display={
          value ? (
            accent ? <span style={{ color: 'var(--accent)' }}>{value}</span> : undefined
          ) : fallback ? (
            <span style={{ color: 'var(--text-dim)' }}>{fallback}</span>
          ) : undefined
        }
        onSave={onSave}
      />
    </div>
  );
}

export function InlineMoney({
  cents,
  onSave,
  exact,
  valueClass = 'gold',
  emptyLabel = '—',
  label,
  context,
}: {
  cents: number | null;
  onSave: (cents: number | null) => void;
  /** Always show two decimals (invoice rail per 06-invoice-detail). */
  exact?: boolean;
  valueClass?: string;
  /** Display text when unset (Stats goal cards use "— click to add"). */
  emptyLabel?: string;
  /** Mobile FieldSheet only: sheet title. Defaults to the RailField label. */
  label?: string;
  /** Mobile FieldSheet only: mono eyebrow under the title, e.g. "ex GST". */
  context?: string;
}) {
  const isMobile = useIsMobile();
  const railLabel = useContext(FieldLabelContext)?.label;
  const [sheet, setSheet] = useState(false);
  const edit = useInlineEdit(cents === null ? '' : (cents / 100).toString(), (raw) =>
    onSave(parseMoney(raw)),
  );

  if (isMobile) {
    return (
      <>
        <FieldTap valueClass={valueClass} empty={cents === null} onPress={() => setSheet(true)}>
          {cents === null ? (
            // The rail's own empty state is a faint "Add"; a caller that set
            // its own copy (Stats' "— click to add") keeps it.
            emptyLabel === '—' ? ADD : emptyLabel
          ) : exact ? (
            formatMoneyExact(cents)
          ) : (
            formatMoney(cents)
          )}
        </FieldTap>
        <FieldSheet
          open={sheet}
          title={label ?? railLabel ?? 'Edit'}
          context={context}
          kind="money"
          value={cents === null ? '' : (cents / 100).toString()}
          onSave={(raw) => onSave(parseMoney(raw))}
          onClose={() => setSheet(false)}
        />
      </>
    );
  }

  if (edit.editing) {
    return (
      <>
        <input
          className="inline-input money"
          value={edit.draft}
          onChange={(e) => edit.setDraft(e.target.value)}
          onKeyDown={edit.onKeyDown}
          onBlur={edit.commit}
          autoFocus
        />
        <div className="inline-hint">Enter to save · Esc to cancel</div>
      </>
    );
  }
  return (
    <div className={`field-value ${valueClass} editable`} onClick={edit.open}>
      {cents === null ? (
        <span style={{ color: 'var(--text-faint)' }}>{emptyLabel}</span>
      ) : exact ? (
        formatMoneyExact(cents)
      ) : (
        formatMoney(cents)
      )}
    </div>
  );
}

// ── Branded date picker (phase 18) — Dropdown-family calendar panel:
//    mono weekday header, day grid, today ring, teal selected, month
//    arrows. Esc closes, arrow keys move the focused day, Enter picks.
//    Pure YYYY-MM-DD string maths — business dates stay Melbourne-local.

export function CalendarPanel({
  value,
  onPick,
  onClose,
  allowClear,
  anchorRef,
  seed,
  title,
  context,
  chips,
  verb,
}: {
  value: string | null;
  onPick: (iso: string | null) => void;
  onClose: () => void;
  allowClear?: boolean;
  /** Element to position the body-level portal against. */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Where an empty field opens instead of today — a shoot's end date, say,
      starts on the shoot date rather than a month away from it. */
  seed?: string | null;
  /** Mobile DateSheet only (Phase 0 §0.3.2): sheet title, defaults "Date". */
  title?: string;
  /** Mobile DateSheet only: mono eyebrow under the title. */
  context?: string;
  /** Mobile DateSheet only: replaces the default quick chips (snooze, follow-ups). */
  chips?: DateChip[];
  /** Mobile DateSheet only: primary button verb, default "Set". */
  verb?: string;
}) {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const today = melbourneToday();
  const openOn = value ?? seed ?? today;
  const [focus, setFocus] = useState(openOn);
  const [viewY, setViewY] = useState(Number(openOn.slice(0, 4)));
  const [viewM, setViewM] = useState(Number(openOn.slice(5, 7)));
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Fixed positioning measured against the viewport: escapes any
  // overflow:hidden ancestor (the convert/new-record modals clip absolutely
  // positioned children) and flips upward when there's no room below.
  useLayoutEffect(() => {
    if (isMobile) return; // The DateSheet owns the mobile render — nothing to place.
    const place = () => {
      const panel = ref.current;
      const anchor = anchorRef?.current ?? panel?.parentElement;
      if (!panel || !anchor) return;
      const a = anchor.getBoundingClientRect();
      const h = panel.offsetHeight;
      const w = panel.offsetWidth;
      const fitsBelow = a.bottom + 6 + h <= window.innerHeight - 8;
      const fitsAbove = a.top - 6 - h >= 8;
      const top = fitsBelow || !fitsAbove ? a.bottom + 6 : a.top - h - 6;
      const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    // Capture phase: page content scrolls inside .page-body, not window.
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, isMobile]);

  const showMonth = (iso: string) => {
    setViewY(Number(iso.slice(0, 4)));
    setViewM(Number(iso.slice(5, 7)));
  };

  useEffect(() => {
    if (isMobile) return; // Sheet owns backdrop-tap dismissal on mobile.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || anchorRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose, isMobile]);

  useEffect(() => {
    if (isMobile) return; // Sheet owns Escape; there is no focused day to arrow around.
    const onKey = (e: globalThis.KeyboardEvent) => {
      const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key in moves) {
        e.preventDefault();
        setFocus((f) => {
          const next = shiftDays(f, moves[e.key]);
          showMonth(next);
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setFocus((f) => {
          onPick(f);
          onClose();
          return f;
        });
      }
    };
    // Capture phase so Modal's own Escape handler doesn't also fire.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, onPick, isMobile]);

  const monthShift = (delta: number) => {
    const next = shiftMonth(viewY, viewM, delta);
    setViewY(next.year);
    setViewM(next.month);
  };

  const days = monthGrid(viewY, viewM);

  if (isMobile) {
    return (
      <DateSheet
        open
        title={title ?? 'Date'}
        context={context}
        value={value}
        allowClear={allowClear}
        chips={chips}
        verb={verb}
        seed={seed}
        onPick={onPick}
        onClose={onClose}
      />
    );
  }

  return createPortal(
    <div
      className="datepick"
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : { position: 'fixed', visibility: 'hidden' }}
    >
      <div className="dp-head">
        <button type="button" className="dp-nav" onClick={() => monthShift(-1)}>
          ‹
        </button>
        <span className="dp-month">
          {MONTHS[viewM - 1]} {viewY}
        </span>
        <button type="button" className="dp-nav" onClick={() => monthShift(1)}>
          ›
        </button>
      </div>
      <div className="dp-grid">
        {DP_DOW.map((d) => (
          <span className="dp-dow" key={d}>
            {d}
          </span>
        ))}
        {days.map((iso) => {
          const out = Number(iso.slice(5, 7)) !== viewM;
          const cls = [
            'dp-day',
            out ? 'out' : '',
            iso === today ? 'today' : '',
            iso === value ? 'selected' : '',
            iso === focus ? 'focused' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              type="button"
              className={cls}
              key={iso}
              onClick={() => {
                onPick(iso);
                onClose();
              }}
            >
              {Number(iso.slice(8, 10))}
            </button>
          );
        })}
      </div>
      <div className="dp-foot">
        <button
          type="button"
          className="dp-link"
          onClick={() => {
            onPick(today);
            onClose();
          }}
        >
          Today
        </button>
        {allowClear && value && (
          <button
            type="button"
            className="dp-link"
            onClick={() => {
              onPick(null);
              onClose();
            }}
          >
            Clear
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Click-to-edit date: shows the formatted date, opens the branded picker. */
export function InlineDate({
  value,
  onSave,
  allowClear,
  suffix,
  style,
  seed,
  compact,
  label,
  context,
  chips,
  verb,
}: {
  value: string | null;
  onSave: (iso: string | null) => void;
  allowClear?: boolean;
  /** Month the picker opens on while the field is empty (see CalendarPanel). */
  seed?: string | null;
  /** Day-month only ("5 Sep") for cells too tight for the year. */
  compact?: boolean;
  /** Appended after the date in display mode (e.g. " · done"). */
  suffix?: string;
  /** Extra display styling (e.g. danger color while overdue). */
  style?: CSSProperties;
  /** Mobile DateSheet only: sheet title. Defaults to the RailField label. */
  label?: string;
  /** Mobile DateSheet only: mono eyebrow under the title. */
  context?: string;
  /** Mobile DateSheet only: replaces the default quick chips. */
  chips?: DateChip[];
  /** Mobile DateSheet only: primary button verb, default "Set". */
  verb?: string;
}) {
  const isMobile = useIsMobile();
  const railLabel = useContext(FieldLabelContext)?.label;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  if (isMobile) {
    return (
      <>
        <FieldTap valueClass="mono" style={style} empty={!value} onPress={() => setOpen(true)}>
          {value ? (compact ? formatDayMonth(value) : formatDate(value)) + (suffix ?? '') : ADD}
        </FieldTap>
        <DateSheet
          open={open}
          title={label ?? railLabel ?? 'Date'}
          context={context}
          value={value}
          allowClear={allowClear}
          chips={chips}
          verb={verb}
          seed={seed}
          onPick={onSave}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="dropdown-anchor" ref={anchorRef}>
      <div className="field-value mono editable" style={style} onClick={() => setOpen((o) => !o)}>
        {value ? (
          (compact ? formatDayMonth(value) : formatDate(value)) + (suffix ?? '')
        ) : (
          <span style={{ color: 'var(--text-faint)' }}>—</span>
        )}
      </div>
      {open && (
        <CalendarPanel
          value={value}
          allowClear={allowClear}
          anchorRef={anchorRef}
          seed={seed}
          onPick={onSave}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** Modal-form date field: f-select chrome, branded picker on click. */
export function DateField({
  value,
  onChange,
  placeholder,
  label,
  context,
  chips,
  verb,
  keyboardNavigation,
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
  placeholder?: string;
  /** Mobile DateSheet only: sheet title. Defaults to the placeholder. */
  label?: string;
  /** Mobile DateSheet only: mono eyebrow under the title. */
  context?: string;
  /** Mobile DateSheet only: replaces the default quick chips. */
  chips?: DateChip[];
  /** Mobile DateSheet only: primary button verb, default "Set". */
  verb?: string;
  /** Opt-in native button for form flows that require keyboard date selection. */
  keyboardNavigation?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  return (
    <div className="dropdown-anchor" ref={anchorRef}>
      {keyboardNavigation ? <button type="button" className="f-select mono" aria-label={label} aria-expanded={open}
        aria-haspopup="dialog" onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.defaultPrevented) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
        }}>
        {value ? <span>{formatDate(value)}</span> : <span className="ph">{placeholder ?? 'Pick date…'}</span>}
        <span className="caret">▾</span>
      </button> : <div className="f-select mono" onClick={() => setOpen((o) => !o)}>
        {value ? <span>{formatDate(value)}</span> : <span className="ph">{placeholder ?? 'Pick date…'}</span>}
        <span className="caret">▾</span>
      </div>}
      {open && (
        <CalendarPanel
          value={value}
          allowClear
          anchorRef={anchorRef}
          onPick={onChange}
          onClose={() => setOpen(false)}
          title={label ?? stripEllipsis(placeholder) ?? 'Date'}
          context={context}
          chips={chips}
          verb={verb}
        />
      )}
    </div>
  );
}

/** "Pick date…" → "Pick date" — a placeholder reads as a sheet title once
    its trailing ellipsis is gone (§0.3.1). */
function stripEllipsis(s: string | undefined): string | undefined {
  return s?.replace(/[….]+$/, '').trim() || undefined;
}

/** Rail-field select: plain value text, click opens the dropdown menu. */
export function InlineSelect<T extends string>({
  items,
  value,
  onSave,
  display,
  valueClass,
  label,
  searchable,
}: {
  items: readonly DropdownItem<T>[];
  value: T | null;
  onSave: (v: T) => void;
  display?: ReactNode;
  valueClass?: string;
  /** Mobile PickerSheet only: sheet title. Defaults to the RailField label. */
  label?: string;
  /** Mobile PickerSheet only: force the search field (it appears on its own
      past eight items). */
  searchable?: boolean;
}) {
  const isMobile = useIsMobile();
  const railLabel = useContext(FieldLabelContext)?.label;
  const [open, setOpen] = useState(false);
  const current = items.find((i) => i.value === value);

  if (isMobile) {
    return (
      <>
        <FieldTap valueClass={valueClass} empty={!current && display === undefined} onPress={() => setOpen(true)}>
          {display ?? current?.label ?? ADD}
        </FieldTap>
        <PickerSheet
          open={open}
          title={label ?? railLabel ?? 'Select'}
          items={items}
          value={value}
          searchable={searchable}
          onSelect={onSave}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="dropdown-anchor">
      <div
        className={`field-value editable${valueClass ? ` ${valueClass}` : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {display ?? current?.label ?? <span style={{ color: 'var(--text-faint)' }}>—</span>}
      </div>
      {open && <Dropdown items={items} value={value} onSelect={onSave} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** Modal-form select with the mockup dropdown menu. */
export function SelectField<T extends string>({
  items,
  value,
  onChange,
  placeholder,
  mono,
  searchable,
  label,
}: {
  items: readonly DropdownItem<T>[];
  value: T | null;
  onChange: (v: T) => void;
  placeholder?: string;
  mono?: boolean;
  searchable?: boolean;
  /** Mobile PickerSheet only: sheet title. Defaults to the placeholder
      without its trailing ellipsis, else "Select". */
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  // The menu flips up near the fold, so the caret points at where it went.
  const [dir, setDir] = useState<'down' | 'up'>('down');
  const current = items.find((i) => i.value === value);
  return (
    <div className="dropdown-anchor">
      <div className={`f-select${mono ? ' mono' : ''}`} onClick={() => setOpen((o) => !o)}>
        {current ? <span>{current.label}</span> : <span className="ph">{placeholder ?? 'Select…'}</span>}
        <span className="caret">{open && dir === 'up' ? '▴' : '▾'}</span>
      </div>
      {open && (
        <Dropdown
          label={label ?? stripEllipsis(placeholder) ?? 'Select'}
          items={items}
          value={value}
          onSelect={onChange}
          onPlacement={setDir}
          onClose={() => {
            setOpen(false);
            setDir('down');
          }}
          searchable={searchable}
        />
      )}
    </div>
  );
}

// ── Notes (click to edit, autosaves) — lead + project detail ─────────────

export function NotesBlock({
  notes,
  onSave,
}: {
  notes: string | null;
  onSave: (v: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queueSave = (value: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSave(value.trim() === '' ? null : value), 900);
  };

  return (
    <Group
      title="Notes"
      link={
        <a className="group-link" onClick={() => setEditing((e) => !e)} style={{ cursor: 'pointer' }}>
          {editing ? 'Done' : 'Edit'}
        </a>
      }
    >
      {editing ? (
        <textarea
          className="notes-box"
          style={{ width: '100%', minHeight: 160, resize: 'vertical', outline: 'none', borderColor: 'var(--accent)' }}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            queueSave(e.target.value);
          }}
          onBlur={() => {
            if (timer.current) clearTimeout(timer.current);
            onSave(draft.trim() === '' ? null : draft);
            setEditing(false);
          }}
          autoFocus
        />
      ) : (
        <div
          className="notes-box editable"
          onClick={() => {
            setDraft(notes ?? '');
            setEditing(true);
          }}
        >
          {notes ? (
            notes.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)
          ) : (
            <p style={{ color: 'var(--text-faint)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>
              No notes yet — click to add.
            </p>
          )}
        </div>
      )}
      <div className="notes-hint">Plain text · autosaves</div>
    </Group>
  );
}

// ── Bulk-select action bar (Phase 1.13) — mockups §2. One shared component
//    for Tasks, Finance (invoices/expenses) and Leads list view; each page
//    supplies its own action set since what's safe differs per entity. ────

export interface BulkAction {
  key: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export function BulkActionBar({
  count,
  actions,
  onClear,
}: {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
}) {
  const isMobile = useIsMobile();
  const [rendered, setRendered] = useState(count > 0);
  const [closing, setClosing] = useState(false);
  const [displayCount, setDisplayCount] = useState(count);

  useEffect(() => {
    if (count > 0) {
      setDisplayCount(count);
      setRendered(true);
      setClosing(false);
      return;
    }

    if (!rendered) return;

    setClosing(true);
    const timeout = window.setTimeout(() => {
      setRendered(false);
      setClosing(false);
    }, 140);
    return () => window.clearTimeout(timeout);
  }, [count, rendered]);

  // The docked mobile bar takes the tab bar's slot, so flag the body while
  // it is up: TabBar hides on it and .content gains tail room (§0.5.4).
  useEffect(() => {
    if (!isMobile || !rendered) return;
    document.body.dataset.bulkOpen = '1';
    return () => {
      delete document.body.dataset.bulkOpen;
    };
  }, [isMobile, rendered]);

  if (!rendered) return null;

  // ≤880px (mockup §3.1 D): equal-width buttons docked at the bottom, no
  // keyboard hints. The bar itself carries "N selected" and a Done that
  // clears the selection — the tab bar is hidden while it's up, so this
  // must be the way out even before a page wires PageHeader.selection.
  if (isMobile) {
    const accentKey = actions.find((a) => !a.danger)?.key;
    return (
      <div
        className={`bulkbar${closing ? ' closing' : ''}`}
        role="toolbar"
        aria-label="Bulk actions"
      >
        <div className="bulkbar-head">
          <span className="bulkbar-count">{count > 0 ? count : displayCount} selected</span>
          <button type="button" className="bulkbar-done" onClick={onClear}>
            Done
          </button>
        </div>
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`cbtn wide${a.danger ? ' dng' : a.key === accentKey ? ' acc' : ''}`}
            onClick={a.onClick}
          >
            {a.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`bulk-floating-bar${closing ? ' closing' : ''}`}
      role="toolbar"
      aria-label="Bulk actions"
    >
      <span className="bulk-count">{count > 0 ? count : displayCount} selected</span>
      <div className="bulk-actions">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`bulk-btn${a.danger ? ' danger' : ''}`}
            onClick={a.onClick}
          >
            {a.label}
          </button>
        ))}
      </div>
      <span className="bulk-kbd-hints">
        <kbd>j</kbd>/<kbd>k</kbd> move focus · <kbd>x</kbd> toggle select · <kbd>Enter</kbd> open · <kbd>Esc</kbd> clear
      </span>
      <span className="bulk-clear" onClick={onClear}>
        Esc to clear ×
      </span>
    </div>
  );
}

// ── Formatters ───────────────────────────────────────────────────────────

// Shared with the worker (ICS feed labels) — single source in shared/format.
export { formatInvoiceNumber } from '../../shared/format';
